<template>
    <div>
        <div class="form-group row">
            <div class="col-4"></div>
            <div class="col-8"><small>{{ $t('general.required_fields') }}</small></div>
        </div>

        <div class="form-group row">
            <label class="form-control-label col-4" for="name">{{ $t('ceremony.deceased_name') }}*</label>
            <div class="col-8">
                <input
                    id="name"
                    :disabled="disabled || ceremony?.created_by === 'api'"
                    type="text"
                    class="form-control form-control-sm"
                    v-model="localValue.name"
                    @change="save"
                />
                <form-errors :errors="errors" field="name" />
            </div>
        </div>

        <div v-if="!ceremony" class="form-group row">
            <label class="form-control-label col-4" for="type">{{ $t('ceremony.type') }}*</label>
            <div class="col-8">
                <select
                    :disabled="disabled || ceremony?.created_by === 'api'"
                    id="type"
                    class="form-control form-control-sm"
                    v-model="localValue.type"
                    @change="save"
                >
                    <option value="ceremony">{{ $t('ceremony.type_ceremony') }}</option>
                    <option value="condolence">{{ $t('ceremony.type_condolence') }}</option>
                    <option value="ledwall" v-if="canBookTypeLedWall">{{ $t('ceremony.type_ledwall') }}</option>
                </select>
                <form-errors :errors="errors" field="type" />
            </div>
        </div>

        <div class="form-group row" v-if="store.state.locations.length > 1">
            <label class="form-control-label col-4" for="location_id">{{ $t('ceremony.location') }}*</label>
            <div class="col-8">
                <select
                    :disabled="disabled || ceremony?.created_by === 'api' || isInPast"
                    id="location_id"
                    class="form-control form-control-sm"
                    v-model="localValue.location_id"
                    @change="updateLocation"
                >
                    <option :value="null">{{ $t('ceremony.choose') }}</option>
                    <option
                        :value="location.id"
                        :key="location.id"
                        v-for="location of store.state.locations"
                        :disabled="ceremony?.id && currentLocation?.type === LOCATION_TYPES.LITE && location.type !== LOCATION_TYPES.LITE"
                    >
                        {{ location.name }}
                    </option>
                </select>

                <form-errors :errors="errors" field="location_id" />

                <div v-if="ceremony?.id && currentLocation?.type === LOCATION_TYPES.LITE && !hasOnlyLiteLocations" class="alert alert-info mt-2">
                    {{ $t('ceremony.not_possible_to_update_a_lite_ceremony_location') }}
                </div>
            </div>
        </div>

        <div class="form-group row">
            <label class="form-control-label col-4" for="state_id">{{ $t('ceremony.status') }}</label>
            <div class="col-8">
                <select
                    :disabled="disabled"
                    id="state_id"
                    class="form-control form-control-sm"
                    v-model="localValue.state_id"
                    @change="save"
                >
                    <option :value="state.id" :key="state.id" v-for="state of store.state.states">
                        {{ $t('ceremony.state_' + state.state) }}
                    </option>
                </select>
                <form-errors :errors="errors" field="state_id" />
            </div>
        </div>

        <div class="form-group row">
            <div class="col-4" />
            <div class="col-8">
                <div class="custom-control custom-checkbox">
                    <input
                        type="checkbox"
                        class="custom-control-input"
                        id="date_unknown_enabled"
                        style="cursor: pointer"
                        v-model="localValue.date_unknown"
                        @change="updateDateUnknown"
                        :disabled="disabled || ceremony?.created_by === 'api'"
                    />
                    <label class="custom-control-label" for="date_unknown_enabled">
                        {{ $t('ceremony.unknown_date') }}
                        <sup>
                            <icon name="question-circle" v-tooltip="$t('ceremony.date_unknown_tooltip')" />
                        </sup>
                    </label>
                </div>
            </div>
        </div>

        <div class="form-group row">
            <label class="form-control-label col-4" for="date">{{ $t('ceremony.date') }}*</label>
            <div class="col-8">
                <datetime-picker
                    v-if="!localValue.date_unknown"
                    v-model="localValue.date"
                    id="date"
                    type="datetime"
                    :disabled="disabled || ceremony?.created_by === 'api'"
                    inputClass="form-control form-control-sm"
                    class="theme-company"
                    :format="'dd-MM-yyyy HH:mm'"
                    :key="localValue.date_unknown"
                    @input="save"
                    :hide-remove-button="ceremony?.created_by === 'api'"
                />
                <input v-else type="text" class="form-control form-control-sm" :value="$t('ceremony.unknown_date')" readonly />
                <form-errors :errors="errors" field="date" />
            </div>
        </div>

        <div class="form-group row" v-if="localValue.type !== 'condolence'">
            <label class="form-control-label col-4" for="duration">{{ $t('ceremony.duration') }}*</label>
            <div class="col-8">
                <minute-input
                    id="duration"
                    v-model="localValue.duration"
                    :class="'form-control-sm'"
                    :disabled="disabled || ceremony?.created_by === 'api'"
                    @change="save"
                />
                <form-errors :errors="errors" field="duration" />
                <small>{{ $t('ceremony.duration_hint') }}</small>
            </div>
        </div>

        <div class="form-group row" v-if="!localValue.date_unknown && selectedLocation?.livestream_channel_id">
            <div class="col-4" />
            <div class="col-8">
                <div class="custom-control custom-checkbox">
                    <input
                        type="checkbox"
                        class="custom-control-input"
                        id="livestreaming_enabled"
                        style="cursor: pointer"
                        v-model="localValue.livestream_enabled"
                        @change="updateLivestreamEnabled"
                    />
                    <label class="custom-control-label" for="livestreaming_enabled">
                        {{ $t('ceremony.livestream') }}
                        <sup>
                            <icon name="question-circle" v-tooltip="$t('ceremony.livestream_tooltip')" />
                        </sup>
                    </label>
                </div>

                <CeremonyLivestreamLink v-if="ceremony?.livestream_event_id" :ceremony="ceremony" />
            </div>
        </div>

        <h6>{{ $t('ceremony.contact_person') }}</h6>

        <small class="d-block mb-3">{{ contactPersonHint }}</small>

        <CeremonyAttendeeFields
            :attendee-index="0"
            :sub-title="$t('ceremony.designer')"
            :is-required="true"
            v-model="localValue.attendees[0]"
            :disabled="disabled || ceremony?.created_by === 'api'"
            :errors="errors"
            :can-send-invite="!ceremony?.id"
            @input="syncAttendeesWithMain"
            @change="save"
        />

        <div v-if="localValue.livestream_enabled && localValue.attendees.length > 1" class="mt-4">
            <CeremonyAttendeeFields
                :attendee-index="1"
                :sub-title="$t('ceremony.livestream')"
                :is-required="false"
                v-model="localValue.attendees[1]"
                :disabled="disabled || ceremony?.created_by === 'api'"
                :errors="errors"
                :can-send-invite="!ceremony?.id"
                @change="save"
            />
        </div>
    </div>
</template>
<script setup>
    import MinuteInput from './MinuteInput.vue';
    import DatetimePicker from './DatetimePicker.vue';
    import FormErrors from './FormErrors.vue';
    import { computed, ref, watch } from 'vue';
    import store from '../store/store';
    import i18n from '../utils/locale';
    import { LOCATION_TYPES } from '../enums/location-types';
    import moment from 'moment';
    import CeremonyAttendeeFields from '../forms/CeremonyAttendeeFields.vue';
    import CeremonyLivestreamLink from './CeremonyLivestreamLink.vue';

    const emit = defineEmits(['input', 'change']);
    const props = defineProps({
        value: {
            type: Object,
            required: true,
        },

        ceremony: {
            type: Object,
            default: null,
        },

        disabled: {
            type: Boolean,
            default: false,
        },

        errors: {
            type: Object,
            default: null,
        },
    });

    const canBookTypeLedWall = computed(() => store.state.companySettings?.can_book_type_ledwall);
    const ceremonyTranslatedType = computed(() => {
        const type = localValue.value?.type;

        switch (type) {
            case 'ceremony':
                return i18n.t('ceremony.type_ceremony');
            case 'condolence':
                return i18n.t('ceremony.type_condolence');
            case 'ledwall':
                return i18n.t('ceremony.type_ledwall');
            default:
                return i18n.t('ceremony.type_unknown');
        }
    });

    const contactPersonHint = computed(() => {
        const values = {
            name: ceremonyTranslatedType.value.toLowerCase(),
        };

        if (localValue.value.livestream_enabled) {
            return i18n.t('ceremony.contact_person_hint_with_livestream', values);
        }

        return i18n.t('ceremony.contact_person_hint', values);
    });

    const selectedLocation = computed(() => {
        return (store.state?.locations || []).find(location => location.id === localValue.value.location_id);
    });

    const currentLocation = computed(() => {
        if (!props.ceremony?.id) {
            return null;
        }

        return (store.state?.locations || []).find(location => location.id === props.ceremony.location_id);
    });

    const hasOnlyLiteLocations = computed(() => {
        return (store.state?.locations || []).every(location => location.type === LOCATION_TYPES.LITE);
    });

    const isInPast = computed(() => {
        if (!props.ceremony?.id) {
            return false;
        }

        return moment(props.ceremony.date).add(props.ceremony.duration, 'seconds').isSameOrBefore(moment());
    });

    const localValue = ref({ ...props.value });

    watch(() => props.value, () => {
        localValue.value = { ...props.value };
    });

    function updateLocation() {
        if (selectedLocation.value?.livestream_channel_id && localValue.value.livestream_enabled) {
            localValue.value.livestream_enabled = false;
        }

        // if the location has a livestream channel id and the livestream has a location and the default of a livestream is true then set the livestream enabled to true.
        if (selectedLocation.value?.livestream_channel_id && store.state.companySettings?.set_default_livestream_enabled && props.ceremony) {
            localValue.value.livestream_enabled = true;
        }

        syncAttendeesWithMain();
        save();
    }

    function updateDateUnknown() {
        if (localValue.value.date_unknown) {
            localValue.value.livestream_enabled = false;
        }

        updateDate();
    }

    function updateDate() {
        if (!localValue.value.date_unknown) {
            localValue.value.date = null;
        }

        save();
    }

    function updateLivestreamEnabled() {
        if (localValue.value.attendees.length <= 1) {
            localValue.value.attendees.push({
                name: null,
                email: null,
                send_invite: selectedLocation.value?.livestream_send_invite,

                nameDirty: false,
                emailDirty: false,
            });
        }

        syncAttendeesWithMain();
        save();
    }

    function syncAttendeesWithMain() {
        for (const attendeeIndex in localValue.value.attendees) {
            if (attendeeIndex === 0) {
                continue;
            }

            if (!localValue.value.attendees[attendeeIndex].nameDirty || !localValue.value.attendees[attendeeIndex]?.name?.length) {
                localValue.value.attendees[attendeeIndex].name = localValue.value.attendees[0].name;
            }

            if (!localValue.value.attendees[attendeeIndex].emailDirty || !localValue.value.attendees[attendeeIndex]?.email?.length) {
                localValue.value.attendees[attendeeIndex].email = localValue.value.attendees[0].email;
            }
        }
    }

    function save() {
        emit('input', localValue.value);
        emit('change', localValue.value);
    }
</script>
